import { useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { getPortalState } from '../../lib/portalDomain'
import { CurrentUser } from '../../lib/currentUser'
import { enhancedApi as webApi, useLazyAuthGetCurrentQuery } from '../redux/api/web-api'
import { normalizeEntitlementsState, setEntitlements } from '../redux/features/entitlements/entitlementsSlice'
import { setUser } from '../redux/features/user/userSlice'
import { triggerAuthChange } from '../utils/broadcastChannel'

import { buildPortalSessionSnapshot, canHydratePortalSession } from './portalSessionState'

type ApplyOrganisationSelectionArgs = {
    organizationId: string
    broadcastAuthChange?: boolean
}

// Apply the selected organisation by rehydrating auth/current from the backend-owned session snapshot.
export const useApplyOrganisationSelection = () => {
    const dispatch = useDispatch()
    const portal = getPortalState(window.location.pathname)
    const [getAuthCurrent] = useLazyAuthGetCurrentQuery()

    return useCallback(
        async ({
            organizationId,
            broadcastAuthChange = true,
        }: ApplyOrganisationSelectionArgs): Promise<CurrentUser> => {
            const sessionData = await getAuthCurrent(undefined, false).unwrap()
            const sessionSnapshot = buildPortalSessionSnapshot(sessionData, {
                currentDomain: portal.currentDomain,
                currentPortalAudience: portal.currentPortalAudience,
            })

            const hydratedOrganizationId =
                sessionSnapshot.selectedOrganizationId ?? sessionSnapshot.sessionEntitlements?.organizationId ?? null

            if (!sessionSnapshot.sessionUser || !canHydratePortalSession(sessionSnapshot, false)) {
                throw new Error('Updated organisation session could not be hydrated')
            }

            if (hydratedOrganizationId !== organizationId) {
                throw new Error('Updated organisation session did not reflect the selected organisation')
            }

            dispatch(setUser(sessionSnapshot.sessionUser))
            dispatch(
                setEntitlements(
                    normalizeEntitlementsState(
                        sessionSnapshot.sessionEntitlements,
                        sessionSnapshot.selectedOrganizationId
                    )
                )
            )
            dispatch(webApi.util.resetApiState())

            if (broadcastAuthChange) {
                triggerAuthChange()
            }

            return sessionSnapshot.sessionUser
        },
        [dispatch, getAuthCurrent, portal.currentDomain, portal.currentPortalAudience]
    )
}
